import type { Dict } from "../core.js";
import type { partnerReport as vi } from "../vi/partnerReport.js";

export const partnerReport: Dict<typeof vi> = {
  title: "Partner report",
  intro: "Every figure the bank partners report (Tingee, SePay) next to what Bube received — by provider and by company. No need to log in to partner portals and count by hand.",
  month: "Month",
  loadFailed: "Could not load the partner report.",
  refresh: "Refresh",
  refreshing: "Queuing…",
  refreshQueued: "Capture of {provider} queued — new figures in a few minutes.",
  refreshCooldown: "{provider} was just refreshed — try again after {at}.",
  stale: "Stale",
  lastCollected: "Captured at {at}",
  neverCollected: "No snapshot yet — the job captures every 6 hours, or press « Refresh ».",
  truncated: "The partner returned too many pages — figures may be INCOMPLETE (no « missing at partner » conclusions).",
  "tab.overview": "Overview",
  "tab.merchants": "Companies",
  "tab.discrepancies": "Reconciliation",
  "tab.apis": "API sources",
  "tab.budgets": "Budgets",
  "tab.wallet": "Wallets & quotas",
  "env.live": "Live",
  "env.test": "Test",
  "provider.tingee": "Tingee",
  "provider.sepay_bankhub": "SePay Bank Hub",
  "provider.sepay_webhook": "SePay (customer-authorized)",
  "provider.gpmpay_webhook": "GPMPay",
  merchants: "Companies",
  users: "Users",
  branches: "Branches",
  shops: "Shops at partner",
  accounts: "Accounts",
  vas: "VAs",
  devices: "Devices",
  orphanUnits: "Orphan units at partner",
  txPartner: "Transactions (partner)",
  txBube: "Transactions (Bube)",
  amountPartner: "Money in (partner)",
  amountBube: "Money in (Bube)",
  missedWebhook: "Partner webhooks not delivered",
  rejectedHooks: "Rejected webhook deliveries",
  prevMonth: "Last month: partner {partner} · Bube {bube}",
  unknown: "unknown",
  cost: "Estimated cost this month",
  costPrev: "Last month",
  "costBasis.partner": "based on the partner's count",
  "costBasis.bube": "based on Bube's count (the partner does not report)",
  window: "Partner has figures for {covered}/{elapsed} days of the month{from}",
  windowFrom: " (from {day})",
  txBubeCovered: "Bube same period: {n}",
  txBubeMonth: "Bube whole month",
  nothingToCollect: "Nothing to capture — the partner is not enabled at the hub for this environment, or there are no connections.",
  usersUnknown: "{n} companies unknown (users live in the app that created the company)",
  "origin.partner": "via {app}",
  "origin.ecosystem": "ecosystem link",
  "origin.system": "system company",
  "origin.bube": "signed up at Bube",
  "costBasis.mixed": "mixed: partner for days with figures + Bube for the rest",
  "detail.covered_days": "compared over {n} days with partner figures",
  "detail.revoked_connection": "company {id}'s connection is revoked but still exists at the partner",
  "errorHint.1133": "the key lacks permission for this function — ask Tingee to enable the API group",
  "errorHint.1004": "the key lacks permission for this function — ask Tingee to enable the API group",
  txOutHint: "+ {n} outgoing",
  txBubeSame: "Bube same period",
  noPricing: "No price on the bound credential set — set it under « Partner integrations ».",
  customerPays: "The customer pays the partner — not a Bube cost.",
  quota: "{pct} of the plan quota used",
  "model.per_transaction": "Per transaction",
  "model.monthly_capped": "Monthly, capped",
  "model.monthly_unlimited": "Monthly, unlimited",
  "scope.per_account": "per account",
  "scope.per_company": "per company",
  "scope.per_contract": "per contract",
  account: "Bube's account at the partner",
  accountWebhook: "Account-level webhook",
  "webhook.ok": "points to Bube",
  "webhook.mismatch": "does NOT point to Bube",
  "webhook.missing": "not set up",
  "webhook.inactive": "turned off",
  coverage: "APIs called: {ok}/{total} OK",
  budgetOf: "Budget: {spent} / {limit} ({pct})",
  "level.ok": "within budget",
  "level.warn": "≥ 80% of budget",
  "level.over": "OVER budget",
  company: "Company",
  provider: "Partner",
  connections: "Connections (running · paused · other)",
  quotaCol: "Quota",
  quotaUnlimited: "Unlimited",
  tx: "Transactions partner / Bube same period",
  amount: "Money in partner / Bube",
  costCol: "Cost",
  scopes: "Missing access",
  suspended: "Suspended",
  detail: "Details",
  close: "Close",
  exportCsv: "Export CSV",
  noMerchants: "No company uses this partner yet.",
  filter: "Filter by company name / id",
  detailTitle: "{name} — {month}",
  detailLoading: "Loading details…",
  units: "Units at the partner",
  daily: "By day (Vietnam time)",
  date: "Date",
  owner: "Partner account owner",
  subscription: "Partner plan",
  noUnits: "The partner has not reported any unit for this company.",
  "kind.tx_count": "Transaction count mismatch",
  "kind.partner_unit_orphan": "Partner unit not linked to a Bube company",
  "kind.va_orphan_at_partner": "VA running at the partner, unknown to Bube",
  "kind.va_missing_at_partner": "Connection has a VA the partner does not list",
  "kind.status_mismatch": "Running at Bube, partner says the VA is inactive",
  "kind.webhook_missing": "Webhook missing at the partner",
  "kind.webhook_mismatch": "Webhook mismatch (off / not pointing to Bube)",
  "kind.missed_webhook": "Partner recorded undelivered webhooks",
  "kind.quota_exceeded": "Partner transaction quota reached / exceeded",
  noDiscrepancies: "No mismatches.",
  bubeVsPartner: "Bube {bube} · partner {partner}",
  api: "API",
  purpose: "Used for",
  readStatus: "Status",
  "status.reported": "In the report",
  "status.not_applicable": "Not applicable",
  reason: "Reason",
  lastOutcome: "Last call",
  "outcome.ok": "OK",
  "outcome.forbidden_scope": "missing access",
  "outcome.error": "error",
  "outcome.not_configured": "not configured",
  "outcome.truncated": "truncated",
  "outcome.skipped": "skipped",
  never: "never called",
  budgetsTitle: "Partner cost budgets",
  budgetsIntro: "Monthly budget per product (whole provider) or a cap for one company. Changing a budget = add one effective from a new date; applied budgets are history. Reaching 80% / 100% ⇒ an item under « Needs attention ».",
  product: "Product",
  scopeAll: "Whole provider",
  workspaceId: "Company id (empty = whole provider)",
  monthly: "Budget / month (₫)",
  effectiveFrom: "Effective from",
  note: "Note",
  add: "Add budget",
  remove: "Delete",
  "budget.current": "in effect",
  "budget.future": "upcoming",
  "budget.past": "replaced",
  noBudgets: "No budgets yet.",
  budgetSaved: "Budget saved.",
  budgetInvalid: "Enter the budget as a positive whole number (VND).",
};
